import { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { toast } from "sonner";
import { 
  Building, 
  Users, 
  Mail, 
  Calendar, 
  CreditCard, 
  Clock, 
  CheckCircle, 
  XCircle, 
  AlertTriangle,
  LogOut,
  BarChart3,
  Loader2,
  Shield,
  Eye,
  EyeOff
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

export default function SuperAdminPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loginLoading, setLoginLoading] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [companies, setCompanies] = useState([]);
  const [stats, setStats] = useState(null);
  const [token, setToken] = useState(null);

  // Check for secret key in URL or existing token
  useEffect(() => {
    const secretKey = searchParams.get("key");
    const storedToken = localStorage.getItem("super_admin_token");
    
    if (secretKey) {
      setToken(secretKey);
      setIsAuthenticated(true);
      setLoading(false);
    } else if (storedToken) {
      setToken(storedToken);
      setIsAuthenticated(true);
      setLoading(false);
    } else {
      setLoading(false);
    }
  }, [searchParams]);

  // Fetch data when authenticated
  useEffect(() => {
    if (isAuthenticated && token) {
      fetchData();
    }
  }, [isAuthenticated, token]);

  const fetchData = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      
      const [companiesRes, statsRes] = await Promise.all([
        axios.get(`${API}/api/super-admin/companies`, { headers }),
        axios.get(`${API}/api/super-admin/stats`, { headers })
      ]);
      
      setCompanies(companiesRes.data.companies);
      setStats(statsRes.data);
    } catch (error) {
      console.error("Error fetching data:", error);
      if (error.response?.status === 401 || error.response?.status === 403) {
        handleLogout();
        toast.error("Sessione scaduta, effettua nuovamente l'accesso");
      }
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginLoading(true);
    
    try {
      const response = await axios.post(`${API}/api/super-admin/login`, {
        email,
        password
      });
      
      const newToken = response.data.token;
      localStorage.setItem("super_admin_token", newToken);
      setToken(newToken);
      setIsAuthenticated(true);
      toast.success("Accesso effettuato!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Credenziali non valide");
    } finally {
      setLoginLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("super_admin_token");
    setToken(null);
    setIsAuthenticated(false);
    setCompanies([]);
    setStats(null);
    navigate("/super-admin");
  };

  const getStatusBadge = (status, paymentCompleted) => {
    if (paymentCompleted) {
      return <Badge className="bg-emerald-100 text-emerald-800">Attivo</Badge>;
    }
    
    switch (status) {
      case "trial":
        return <Badge className="bg-blue-100 text-blue-800">Trial</Badge>;
      case "expired":
        return <Badge className="bg-red-100 text-red-800">Scaduto</Badge>;
      case "blocked":
        return <Badge className="bg-red-100 text-red-800">Bloccato</Badge>;
      default:
        return <Badge className="bg-slate-100 text-slate-800">{status}</Badge>;
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString("it-IT", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric"
      });
    } catch {
      return dateStr;
    }
  };

  const getDaysRemaining = (trialEndDate) => {
    if (!trialEndDate) return null;
    try {
      const end = new Date(trialEndDate);
      const now = new Date();
      const days = Math.ceil((end - now) / (1000 * 60 * 60 * 24));
      return days;
    } catch {
      return null;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
      </div>
    );
  }

  // Login form
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 to-slate-800 p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="mx-auto w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center mb-4">
              <Shield className="h-6 w-6 text-emerald-600" />
            </div>
            <CardTitle className="text-2xl">Super-Admin</CardTitle>
            <CardDescription>Accedi alla dashboard di amministrazione</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleLogin} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="admin@ferie-e-permessi.it"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    required
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="absolute right-0 top-0 h-full"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </Button>
                </div>
              </div>
              <Button type="submit" className="w-full" disabled={loginLoading}>
                {loginLoading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Accesso...
                  </>
                ) : (
                  "Accedi"
                )}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Dashboard
  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
                <Shield className="h-5 w-5 text-emerald-600" />
              </div>
              <div>
                <h1 className="text-lg font-bold text-slate-900">Super-Admin Dashboard</h1>
                <p className="text-xs text-slate-500">Ferie e Permessi</p>
              </div>
            </div>
            <Button variant="outline" onClick={handleLogout} className="gap-2">
              <LogOut className="h-4 w-4" />
              Esci
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-500">Aziende Totali</p>
                    <p className="text-3xl font-bold">{stats.total_companies}</p>
                  </div>
                  <Building className="h-8 w-8 text-slate-300" />
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-500">Dipendenti Totali</p>
                    <p className="text-3xl font-bold">{stats.total_employees}</p>
                  </div>
                  <Users className="h-8 w-8 text-slate-300" />
                </div>
              </CardContent>
            </Card>
            <Card className="bg-blue-50 border-blue-200">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-blue-600">In Prova</p>
                    <p className="text-3xl font-bold text-blue-700">{stats.trial_companies}</p>
                  </div>
                  <Clock className="h-8 w-8 text-blue-300" />
                </div>
              </CardContent>
            </Card>
            <Card className="bg-emerald-50 border-emerald-200">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-emerald-600">Attive</p>
                    <p className="text-3xl font-bold text-emerald-700">{stats.active_companies}</p>
                  </div>
                  <CheckCircle className="h-8 w-8 text-emerald-300" />
                </div>
              </CardContent>
            </Card>
            <Card className="bg-red-50 border-red-200">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-red-600">Scadute</p>
                    <p className="text-3xl font-bold text-red-700">{stats.expired_companies}</p>
                  </div>
                  <XCircle className="h-8 w-8 text-red-300" />
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Companies List */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Building className="h-5 w-5" />
              Aziende Registrate
            </CardTitle>
            <CardDescription>
              Lista completa delle aziende registrate sulla piattaforma
            </CardDescription>
          </CardHeader>
          <CardContent>
            {companies.length === 0 ? (
              <div className="text-center py-12 text-slate-500">
                <Building className="h-12 w-12 mx-auto mb-4 text-slate-300" />
                <p>Nessuna azienda registrata</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-slate-200">
                      <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Azienda</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Titolare</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Registrazione</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Fine Trial</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Stato</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Piano</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Dipendenti</th>
                    </tr>
                  </thead>
                  <tbody>
                    {companies.map((company) => {
                      const daysRemaining = getDaysRemaining(company.trial_end_date);
                      
                      return (
                        <tr key={company.id} className="border-b border-slate-100 hover:bg-slate-50">
                          <td className="py-4 px-4">
                            <div className="font-medium text-slate-900">{company.nome_azienda}</div>
                            <div className="text-xs text-slate-400">ID: {company.id}</div>
                          </td>
                          <td className="py-4 px-4">
                            <div className="text-sm text-slate-900">{company.titolare_nome}</div>
                            <div className="text-xs text-slate-500 flex items-center gap-1">
                              <Mail className="h-3 w-3" />
                              {company.titolare_email}
                            </div>
                          </td>
                          <td className="py-4 px-4">
                            <div className="text-sm text-slate-600">
                              {formatDate(company.data_registrazione)}
                            </div>
                          </td>
                          <td className="py-4 px-4">
                            <div className="text-sm text-slate-600">
                              {formatDate(company.trial_end_date)}
                            </div>
                            {daysRemaining !== null && !company.payment_completed && (
                              <div className={`text-xs ${daysRemaining > 0 ? 'text-blue-600' : 'text-red-600'}`}>
                                {daysRemaining > 0 ? `${daysRemaining} giorni rimasti` : 'Scaduto'}
                              </div>
                            )}
                          </td>
                          <td className="py-4 px-4">
                            {getStatusBadge(company.subscription_status, company.payment_completed)}
                          </td>
                          <td className="py-4 px-4">
                            <div className="text-sm text-slate-600">{company.piano_nome}</div>
                          </td>
                          <td className="py-4 px-4">
                            <div className="flex items-center gap-1 text-sm text-slate-600">
                              <Users className="h-4 w-4" />
                              {company.num_dipendenti}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Info Box */}
        <div className="mt-8 p-4 bg-slate-100 rounded-lg text-sm text-slate-600">
          <p className="flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 text-amber-500" />
            <strong>Nota:</strong> I dati vengono aggiornati in tempo reale. Le email di notifica vengono inviate automaticamente a <strong>support@ferie-e-permessi.it</strong> per ogni nuova registrazione.
          </p>
        </div>
      </main>
    </div>
  );
}
